import { customerTypeEnum } from "../enums/customerType.enum";

/**
 * Minimal customer shape needed to name a downloaded report. Structurally compatible with
 * `SelectedCustomerEligibilityData` and with the customer detail payload, so callers can pass
 * either without mapping.
 */
export interface DownloadNamingCustomer {
  name?: string;
  kboNumber?: string;
  customerType?: string;
}

/** Reserved on Windows/macOS filesystems; a customer name containing any of these would break the save. */
const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|]/g;
/** Keeps the full name well inside the 255-byte limit every filesystem enforces, suffix included. */
const MAX_NAME_LENGTH = 120;

const REPORT_LABEL = "AMLCare Verification report";

/**
 * Strips characters that cannot appear in a filename, collapses the whitespace they leave behind and
 * drops trailing dots/spaces (Windows silently trims those, which would corrupt the extension).
 */
function sanitizeForFileName(value: string): string {
  return value
    .replace(ILLEGAL_FILENAME_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[. ]+$/, "")
    .slice(0, MAX_NAME_LENGTH)
    .trim();
}

/** Local calendar date as YYYY-MM-DD — filename-safe and sorts chronologically. */
function formatFileNameDate(date: Date): string {
  const pad = (part: number): string => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Builds the client-facing verification report filename:
 *   `ROYAL SPORTING CLUB ANDERLECHT (BE0823379451) - AMLCare Verification report - 2026-08-12.pdf`
 *   `JEAN DUPONT - AMLCare Verification report - 2026-08-12.pdf`
 *
 * The registration number is only ever appended for companies — for a private person that identifier
 * is the (more sensitive) national number, which must stay out of filenames. The customer schema
 * already clears `kboNumber` for private persons, so the customerType check is belt-and-braces.
 *
 * @param customer Customer the report was generated for.
 * @param createdAt When the report was created; falls back to today when the caller has no value.
 * @param extension File extension including the leading dot.
 * @returns The filename, or null when the customer name is unknown so the caller can keep its own fallback.
 */
export function buildVerificationReportFileName(
  customer: DownloadNamingCustomer | undefined,
  createdAt: string | Date | undefined,
  extension: string
): string | null {
  const name = sanitizeForFileName(customer?.name ?? "");
  if (!name) return null;

  const createdDate = createdAt ? new Date(createdAt) : new Date();
  const date = formatFileNameDate(isNaN(createdDate.getTime()) ? new Date() : createdDate);

  const registrationNumber =
    customer?.customerType === customerTypeEnum.PRIVATE_PERSON ? "" : sanitizeForFileName(customer?.kboNumber ?? "");
  const subject = registrationNumber ? `${name} (${registrationNumber})` : name;

  return `${subject} - ${REPORT_LABEL} - ${date}${extension}`;
}
